import React, { FormEvent } from 'react';
import { Grid, Dialog } from '@material-ui/core';
import { Tooltip, BoxV2 as Box, TextInput, DropdownV2 } from 'portal-commons';
import parse from 'html-react-parser';

import { ModalType, VettingClassType, VettingProvider } from '../../types';
import { SelectItem, EmptyString } from '@/utils/types';
import { estimatedTimeByVettingClass } from '../../utils';

interface Props {
  open?: boolean;
  vettingProviders: Array<VettingProvider>;
  vettingClasses: Array<SelectItem>;
  handleClose: () => void;
  handleChange: (_key: string, _value: string) => void;
  handleError: (_key: string, _value: string) => void;
  submitVettingRequest: (event: FormEvent<HTMLFormElement>) => void;
  newVettingData: { title: string; text: string } | EmptyString;
  importVettingData: { title: string; text: string } | EmptyString;
  extVettingProviderId: string;
  error: Record<string, string>;
  vettingToken: string;
  vettingId: string;
  modalType: ModalType;
  vettingClass: VettingClassType | EmptyString;
  vettingLoader: boolean;
  isBusinessContactEmailRequired?: boolean; // for checking if business contact email input is visible
  businessContactEmail: string; // editing value
  isWebsiteRequired?: boolean;
  website: string;
}

const BrandVettingModal: React.FC<Props> = ({
  open = true,
  vettingProviders,
  vettingClasses,
  handleClose,
  handleChange,
  handleError,
  submitVettingRequest,
  newVettingData,
  importVettingData,
  extVettingProviderId,
  error,
  vettingToken,
  vettingId,
  modalType,
  vettingClass,
  vettingLoader,
  isBusinessContactEmailRequired = false,
  businessContactEmail,
  isWebsiteRequired = false,
  website,
}) => {
  const isNoAvailableVettingProvider =
    !!vettingClass && vettingProviders.length === 0;

  const vettingProvider: VettingProvider | undefined = extVettingProviderId
    ? vettingProviders.find((item) => item.uid === extVettingProviderId)
    : undefined;

  const displayModalTitle = () => {
    if (modalType === ModalType.newVetting) {
      return newVettingData && newVettingData.title
        ? newVettingData.title
        : 'Apply for New Vetting';
    }
    return importVettingData && importVettingData.title
      ? importVettingData.title
      : 'Import Vetting';
  };

  const displayVettingPrice = () => {
    if (
      vettingClass === 'STANDARD' &&
      (extVettingProviderId === 'AEGIS' || extVettingProviderId === 'WMC')
    ) {
      return '$41.50';
    } else if (
      vettingClass === 'ENHANCED' &&
      extVettingProviderId === 'AEGIS'
    ) {
      return `$101.50`;
    } else if (
      vettingClass === 'POLITICAL' &&
      extVettingProviderId === 'AEGIS'
    ) {
      return '$66.00 - $96.00 (Depending on the delivery method)';
    } else if (
      vettingClass === 'AUTHPLUS' &&
      extVettingProviderId === 'AEGIS'
    ) {
      return '$12.50';
    } else if (vettingClass === 'RCS' && extVettingProviderId === 'AEGIS') {
      return '$75.00';
    } else if (
      vettingClass &&
      vettingProvider &&
      vettingProvider.vettingInstruction?.newVet?.price?.[vettingClass]
    ) {
      return `$${vettingProvider.vettingInstruction.newVet.price[vettingClass]}`;
    } else {
      return '--';
    }
  };

  const disableSubmit = () => {
    if (!extVettingProviderId) {
      return true;
    }
    if (modalType === ModalType.importVetting) {
      const vettingTokenCheck =
        extVettingProviderId !== 'AEGIS' || vettingToken;
      if (!vettingId || !vettingTokenCheck) {
        return true;
      }
    }
    if (
      (vettingClass === 'RCS' || vettingClass === 'AUTHPLUS') &&
      ((isBusinessContactEmailRequired && !businessContactEmail) ||
        (isWebsiteRequired && (!website || error.website)))
    ) {
      return true;
    }
    return vettingLoader;
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        !(reason === 'backdropClick') && handleClose();
      }}
      aria-labelledby="responsive-dialog-title"
      className="brand-vetting-modal"
      data-testid="brandVettingModal"
    >
      <div className="modal-wrapper">
        <div className="modal-content">
          <div className="modal-info">
            <Box
              as="h3"
              className="heading1"
              sx={{
                color: 't.black80',
                fontSize: '24px !important',
                fontWeight: 'semiBold',
              }}
            >
              {displayModalTitle()}
            </Box>
            <Box
              className="paragraph"
              sx={{
                fontSize: '16px',
                lineHeight: 'normal',
                color: 't.black80',
                '& > p': {
                  // update the scss and drop this when deprecate the V1 modal
                  fontSize: '16px !important',
                  margin: '0 0 32px 0 !important',
                },
              }}
            >
              {modalType === ModalType.newVetting
                ? newVettingData
                  ? parse(newVettingData.text)
                  : null
                : importVettingData
                ? parse(importVettingData.text)
                : null}
            </Box>
          </div>
          <form noValidate onSubmit={submitVettingRequest}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}
            >
              {modalType === ModalType.newVetting ? (
                <Box
                  sx={{ position: 'relative' }}
                  data-testid="vettingTypeField"
                >
                  <DropdownV2
                    label="Select Vetting Type"
                    value={vettingClass}
                    aria-label="vettingClass"
                    data-testid="vettingTypeSelect"
                    onChange={(value) => handleChange('vettingClass', value)}
                    options={vettingClasses.map((item) => ({
                      value: item.value,
                      label: item.label,
                    }))}
                  />
                  {/* <Tooltip
                      title={'Vetting partners may provide independent information to the carrier about the Brand which may result in the Brand having access to a better commecial terms.'}
                    /> */}
                </Box>
              ) : null}
              <Box
                sx={{ position: 'relative' }}
                data-testid="vettingPartnerField"
              >
                <DropdownV2
                  label="Select Vetting Partner"
                  value={extVettingProviderId}
                  disabled={
                    (vettingClass && vettingProviders.length) ||
                    modalType === ModalType.importVetting
                      ? false
                      : true
                  }
                  aria-label="evpUId"
                  data-testid="vettingPartnerSelect"
                  onChange={(value) => handleChange('evpUid', value)}
                  options={vettingProviders.map((item) => ({
                    value: item.uid,
                    label: item.displayName,
                  }))}
                  error={isNoAvailableVettingProvider}
                  helperText={
                    isNoAvailableVettingProvider
                      ? 'No Vetting Partners available for the Vetting Type'
                      : undefined
                  }
                />
                <Tooltip
                  style={{ top: '12px' }}
                  title={
                    'Vetting partners may provide independent information to the carrier about the Brand which may result in the Brand having access to better commercial terms.'
                  }
                />
              </Box>
              {modalType === ModalType.newVetting &&
                isBusinessContactEmailRequired &&
                (vettingClass === 'RCS' || vettingClass === 'AUTHPLUS') && (
                  <TextInput
                    required={true}
                    maxLength={100}
                    value={businessContactEmail}
                    label="Business Contact Email"
                    aria-label="businessContactEmail"
                    data-testid="businessContactEmail"
                    onChange={(value) =>
                      handleChange('businessContactEmail', value)
                    }
                    onBlur={(value) =>
                      handleError('businessContactEmail', value)
                    }
                    error={!!error.businessContactEmail}
                    note={error.businessContactEmail}
                  />
                )}
              {modalType === ModalType.newVetting &&
                isWebsiteRequired &&
                (vettingClass === 'RCS' || vettingClass === 'AUTHPLUS') && (
                  <TextInput
                    required
                    maxLength={255}
                    value={website}
                    label="Website URL"
                    aria-label="website"
                    data-testid="website"
                    onChange={(value) => handleChange('website', value)}
                    onBlur={(value) => handleError('website', value)}
                    error={!!error.website}
                    note={error.website}
                  />
                )}
              {modalType === ModalType.importVetting ? (
                <TextInput
                  error={error.vettingId ? true : false}
                  value={vettingId}
                  id="vettingId"
                  minRows={1}
                  maxRows={3}
                  multiline
                  label="Vetting Identifier"
                  aria-label="vettingId"
                  data-testid="vettingIdInputField"
                  onChange={(value) => {
                    const updatedText = value.replace(/[\n\r\t\s]+/g, ''); // Remove new lines, tabs, and spaces
                    handleChange('vettingId', updatedText);
                  }}
                  onBlur={(value) => handleError('vettingId', value)}
                  note={error.vettingId}
                />
              ) : null}
              {modalType === ModalType.importVetting &&
              extVettingProviderId === 'AEGIS' ? (
                <TextInput
                  error={error.vettingToken ? true : false}
                  value={vettingToken}
                  id="vettingToken"
                  minRows={1}
                  maxRows={3}
                  multiline
                  label="Vetting Token"
                  aria-label="vettingToken"
                  data-testid="vettingTokenInputField"
                  onChange={(value) => handleChange('vettingToken', value)}
                  onBlur={(value) => handleError('vettingToken', value)}
                  note={error.vettingToken}
                />
              ) : null}
            </Box>

            {vettingProvider && modalType === ModalType.newVetting ? (
              vettingProvider.vettingInstruction.newVet ? (
                <Grid item xs={12}>
                  <div
                    className="vetting-details"
                    data-testid="vettingDetailBlock"
                  >
                    {vettingClass === 'RCS' && (
                      <Box
                        as="p"
                        sx={{
                          marginBottom: 'm',
                          fontSize: '16px',
                          lineHeight: '19px',
                          color: 't.black80',
                        }}
                      >
                        RCS availability is currently limited. Please check with
                        your DCA for availability and support before proceeding.
                      </Box>
                    )}
                    <h3 className="paragraph text-center">
                      <span>Vetting Price: </span>
                      {displayVettingPrice()}
                      {vettingClass !== 'RCS' && (
                        <>
                          <br />
                          <span>Estimated Time for Vetting: </span>
                          {estimatedTimeByVettingClass(
                            vettingClass,
                            extVettingProviderId,
                            vettingProvider
                          )}
                        </>
                      )}
                    </h3>
                  </div>
                </Grid>
              ) : null
            ) : null}
            <Grid item className="modal-footer">
              <ul className="list-inline text-center">
                <li>
                  <a
                    href="#"
                    onClick={() => handleClose()}
                    className="secondary-btn"
                    data-testid="brandVettingModalCancelButton"
                  >
                    Cancel
                  </a>
                </li>
                <li>
                  <div className="form-group-field">
                    <input
                      type="submit"
                      disabled={disableSubmit()}
                      className="primary-btn"
                      data-testid="brandVettingModalSubmitButton"
                      value={
                        vettingClass === 'POLITICAL' &&
                        extVettingProviderId === 'AEGIS'
                          ? 'Next'
                          : 'Submit'
                      }
                    />
                  </div>
                </li>
              </ul>
            </Grid>
          </form>
        </div>
      </div>
    </Dialog>
  );
};
export default BrandVettingModal;
